import { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Heart, X, ExternalLink, Pencil, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { Link } from 'react-router-dom';
import { listClients, createClient, updateClient, deleteClient, adminListAllGalleries, adminFavoritesSummary, adminGetClientFavorites } from '@/lib/api';
import { ADMIN_DASHBOARD, GALLERY_FAVORITES, ADMIN_CLIENT_EDIT } from '@/constants/testIds';

const EMPTY_FORM = { email: '', name: '', gallery_slugs: [] };

export const AdminClientsTab = () => {
  const [clients, setClients] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [favSummary, setFavSummary] = useState({});
  const [favPanel, setFavPanel] = useState(null); // { client, total, galleries } | 'loading'
  const [editClient, setEditClient] = useState(null); // client being edited | null
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);

  const refresh = () => {
    listClients().then(setClients).catch(() => setClients([]));
    adminFavoritesSummary().then(setFavSummary).catch(() => setFavSummary({}));
  };

  useEffect(() => {
    refresh();
    adminListAllGalleries().then(setGalleries).catch(() => setGalleries([]));
  }, []);

  const openFavorites = async (client) => {
    setFavPanel('loading');
    try {
      setFavPanel(await adminGetClientFavorites(client.id));
    } catch {
      toast.error('Could not load favorites.');
      setFavPanel(null);
    }
  };

  const toggleGallerySlug = (slug) => {
    setForm((f) => ({
      ...f,
      gallery_slugs: f.gallery_slugs.includes(slug)
        ? f.gallery_slugs.filter((s) => s !== slug)
        : [...f.gallery_slugs, slug],
    }));
  };

  const toggleEditGallerySlug = (slug) => {
    setEditForm((f) => ({
      ...f,
      gallery_slugs: f.gallery_slugs.includes(slug)
        ? f.gallery_slugs.filter((s) => s !== slug)
        : [...f.gallery_slugs, slug],
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createClient(form);
      toast.success('Client invited. They will set their own password at sign-up.');
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to invite client.');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (client) => {
    setEditClient(client);
    setEditForm({ email: client.email, name: client.name || '', gallery_slugs: client.gallery_slugs || [] });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      await updateClient(editClient.id, { name: editForm.name, gallery_slugs: editForm.gallery_slugs });
      toast.success('Client account updated.');
      setEditClient(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update client account.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Remove client account "${client.email}"? This cannot be undone.`)) return;
    try {
      await deleteClient(client.id);
      toast.success('Client account removed.');
      refresh();
    } catch (err) {
      toast.error('Failed to remove client account.');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[hsl(var(--lxra-muted))]">
          Invite a client by email and link their galleries. They set their own password by signing up at{' '}
          <span className="text-[hsl(var(--lxra-fg))]">/signup</span>.
        </p>
        <Button
          onClick={() => setDialogOpen(true)}
          data-testid={ADMIN_DASHBOARD.createClientButton}
          className="h-10 px-4 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Invite Client
        </Button>
      </div>

      {!clients && <Skeleton className="h-64 rounded-2xl bg-[#141416]" />}

      {clients && (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden" data-testid="admin-clients-table">
          <Table>
            <TableHeader>
              <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                <TableHead className="text-[hsl(var(--lxra-muted))]">Name</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Email</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Status</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Galleries</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Favorites</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.length === 0 && (
                <TableRow className="border-[hsl(var(--lxra-border))]">
                  <TableCell colSpan={6} className="text-center text-[hsl(var(--lxra-muted))] py-10">
                    No client accounts yet.
                  </TableCell>
                </TableRow>
              )}
              {clients.map((c) => (
                <TableRow key={c.id} className="border-[hsl(var(--lxra-border))] hover:bg-[#141416]">
                  <TableCell className="text-[hsl(var(--lxra-fg))]">{c.name || '—'}</TableCell>
                  <TableCell className="text-[hsl(var(--lxra-fg))]">{c.email}</TableCell>
                  <TableCell>
                    {c.activated ? (
                      <Badge
                        data-testid={ADMIN_CLIENT_EDIT.activatedBadge(c.id)}
                        variant="outline"
                        className="border-emerald-500/40 text-emerald-400 text-[10px] gap-1"
                      >
                        <CheckCircle2 className="h-3 w-3" /> Activated
                      </Badge>
                    ) : (
                      <Badge
                        data-testid={ADMIN_CLIENT_EDIT.activatedBadge(c.id)}
                        variant="outline"
                        className="border-[hsl(var(--lxra-muted))]/40 text-[hsl(var(--lxra-muted))] text-[10px] gap-1"
                      >
                        <Clock className="h-3 w-3" /> Pending
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1.5">
                      {c.gallery_slugs.map((s) => (
                        <Badge key={s} variant="outline" className="border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] text-[10px]">
                          {s}
                        </Badge>
                      ))}
                      {c.gallery_slugs.length === 0 && (
                        <span className="text-xs text-[hsl(var(--lxra-muted))]">No galleries linked</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => openFavorites(c)}
                      data-testid={GALLERY_FAVORITES.adminButton(c.id)}
                      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs transition-colors ${
                        favSummary[c.email]
                          ? 'border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10'
                          : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-fg))]'
                      }`}
                    >
                      <Heart className={`h-3.5 w-3.5 ${favSummary[c.email] ? 'fill-current' : ''}`} />
                      {favSummary[c.email] || 0}
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => openEdit(c)}
                      data-testid={ADMIN_CLIENT_EDIT.editButton(c.id)}
                      className="text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(c)}
                      data-testid={`admin-client-delete-${c.id}`}
                      className="text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {favPanel && (
        <div className="mt-6 rounded-2xl border border-[hsl(var(--lxra-gold))]/30 bg-[#0F0F10] p-6" data-testid={GALLERY_FAVORITES.adminPanel}>
          {favPanel === 'loading' ? (
            <Skeleton className="h-40 rounded-xl bg-[#141416]" />
          ) : (
            <>
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">Client Favorites</p>
                  <h3 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">
                    {favPanel.client.name || favPanel.client.email}
                  </h3>
                  <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">
                    {favPanel.total} favorite{favPanel.total === 1 ? '' : 's'} across {favPanel.galleries.length} galler{favPanel.galleries.length === 1 ? 'y' : 'ies'} — use these to guide final edits and album picks.
                  </p>
                </div>
                <button type="button" onClick={() => setFavPanel(null)} aria-label="Close favorites" className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:border-[hsl(var(--lxra-gold))] transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {favPanel.total === 0 && (
                <p className="text-sm text-[hsl(var(--lxra-muted))] py-6 text-center">This client hasn&apos;t favorited any photos yet.</p>
              )}
              <div className="space-y-6">
                {favPanel.galleries.map((g) => (
                  <div key={g.gallery_slug}>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-[hsl(var(--lxra-fg))]">{g.gallery_name} <span className="text-[hsl(var(--lxra-muted))]">· {g.images.length}</span></p>
                      <Link to={`/galleries/${g.gallery_slug}`} target="_blank" className="inline-flex items-center gap-1 text-xs text-[hsl(var(--lxra-gold))] hover:underline">
                        Open gallery <ExternalLink className="h-3 w-3" />
                      </Link>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
                      {g.images.map((img) => (
                        <a key={img.id} href={img.image_url} target="_blank" rel="noopener noreferrer" className="group relative aspect-square overflow-hidden rounded-lg border border-[hsl(var(--lxra-border))]" title={`#${(img.order ?? 0) + 1}`}>
                          <img src={img.image_url} alt={img.caption || ''} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                          <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">#{(img.order ?? 0) + 1}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
          <DialogHeader>
            <DialogTitle className="text-[hsl(var(--lxra-fg))]">Invite Client</DialogTitle>
            <DialogDescription className="text-[hsl(var(--lxra-muted))]">
              Invite a client by email and optionally link a gallery. They set their own password by signing up.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Email</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10"
              />
              <p className="text-[11px] text-[hsl(var(--lxra-muted))]">
                They&apos;ll receive access once they sign up at /signup with this email — no password to set here.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Link Gallery(ies)</Label>
              <div className="flex flex-wrap gap-2">
                {galleries.map((g) => (
                  <button
                    type="button"
                    key={g.slug}
                    onClick={() => toggleGallerySlug(g.slug)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      form.gallery_slugs.includes(g.slug)
                        ? 'bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] border-[hsl(var(--lxra-gold))]'
                        : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:border-[hsl(var(--lxra-gold))]'
                    }`}
                  >
                    {g.slug}
                  </button>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={saving}
                className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Invite'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editClient} onOpenChange={(open) => !open && setEditClient(null)}>
        <DialogContent className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]" data-testid={ADMIN_CLIENT_EDIT.dialog}>
          <DialogHeader>
            <DialogTitle className="text-[hsl(var(--lxra-fg))]">Edit Client</DialogTitle>
            <DialogDescription className="text-[hsl(var(--lxra-muted))]">
              Update this client&apos;s display name and which galleries are linked to their account.
            </DialogDescription>
          </DialogHeader>
          {editClient && (
            <form onSubmit={handleEditSave} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Email</Label>
                <Input value={editForm.email} disabled className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10 opacity-60" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid={ADMIN_CLIENT_EDIT.nameInput}
                  className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Linked Gallery(ies)</Label>
                <div className="flex flex-wrap gap-2" data-testid={ADMIN_CLIENT_EDIT.gallerySelect}>
                  {galleries.map((g) => (
                    <button
                      type="button"
                      key={g.slug}
                      onClick={() => toggleEditGallerySlug(g.slug)}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                        editForm.gallery_slugs.includes(g.slug)
                          ? 'bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] border-[hsl(var(--lxra-gold))]'
                          : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:border-[hsl(var(--lxra-gold))]'
                      }`}
                    >
                      {g.slug}
                    </button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={editSaving}
                  data-testid={ADMIN_CLIENT_EDIT.saveButton}
                  className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
                >
                  {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
